import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { addWorkoutProgress } from "../services/apiService";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const WorkoutHistory = ({ data, userId: propUserId, onUpdate }) => {
  const [userId, setUserId] = useState(propUserId || null);
  const [workoutDescription, setWorkoutDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [duration, setDuration] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [calendarDate, setCalendarDate] = useState("");
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!propUserId && user?.uid) {
      setUserId(user.uid);
    }
  }, [propUserId, user?.uid]);

  const getDurationInMinutes = (durationStr) => {
    if (!durationStr) return 0;
    const [hours, minutes, seconds] = durationStr.split(":").map(Number);
    return hours * 60 + minutes + (seconds || 0) / 60;
  };

  const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const options = { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    };
    return date.toLocaleDateString('en-US', options);
  };

  const calculateDuration = () => {
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);
      const start = startHours * 60 + startMinutes;
      const end = endHours * 60 + endMinutes;
      const diff = end - start;
      
      if (diff >= 0) {
        const hours = Math.floor(diff / 60);
        const minutes = diff % 60;
        setDuration(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`);
      } else {
        toast.error("End time should be after start time.");
      }
    } else {
      toast.error("Please enter both start and end times.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    if (!workoutDescription || !startTime || !endTime || !duration || !calendarDate) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      await addWorkoutProgress(userId, {
        workoutDescription,
        startTime,
        endTime,
        duration,
        calendarDate,
      });

      toast.success("Workout progress added successfully!");

      // Reset form
      setWorkoutDescription("");
      setStartTime("");
      setEndTime("");
      setDuration("");
      setCalendarDate("");
      setShowForm(false);

      // Refresh data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error adding workout progress:", error);
      toast.error("Failed to add workout progress. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Process and sort workout data for chart display
  const processWorkoutData = (workoutData) => {
    if (!workoutData || workoutData.length === 0) return [];

    // Sort by date (oldest first) and process data
    const sortedData = [...workoutData]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((workout) => ({
        ...workout,
        duration: getDurationInMinutes(workout.duration),
        displayDate: formatDateForDisplay(workout.date),
        fullDate: workout.date
      }));

    return sortedData;
  };

  const formattedData = processWorkoutData(data);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{`Date: ${data.fullDate} (${data.day})`}</p>
          <p className="tooltip-duration">{`Duration: ${payload[0].value.toFixed(1)} minutes`}</p>
          <p className="tooltip-description">{`Workout: ${data.workout_description}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="workout-history">
      <h2>Workout History</h2>
      
      {formattedData.length > 0 ? (
        <>
          <h3 style={{ textAlign: 'center', color: '#007acc', marginBottom: '20px' }}>
            Workout Duration Trends
          </h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={450}>
            <LineChart data={formattedData} margin={{ top: 30, right: 40, left: 80, bottom: 80 }}>
              <XAxis 
                dataKey="displayDate" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                label={{ 
                  value: 'Duration (minutes)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
                domain={[0, 'dataMax']}
                tick={{ fontSize: 12 }}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              <Line 
                type="monotone" 
                dataKey="duration" 
                stroke="#007acc" 
                strokeWidth={3}
                dot={{ fill: '#007acc', strokeWidth: 2, r: 5 }}
                activeDot={{ r: 8, stroke: '#007acc', strokeWidth: 2, fill: '#ffffff' }}
                name="Workout Duration"
              />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="workout-summary">
            <p><strong>Total Workouts:</strong> {formattedData.length}</p>
            <p><strong>Average Duration:</strong> {
              (formattedData.reduce((sum, w) => sum + w.duration, 0) / formattedData.length).toFixed(1)
            } minutes</p>
            <p><strong>Date Range:</strong> {formattedData[0]?.displayDate} - {formattedData[formattedData.length - 1]?.displayDate}</p>
          </div>
          </div>
        </>
      ) : (
        <div className="no-data">
          <p>No workout history available. Add your first workout!</p>
        </div>
      )}

      <button
        onClick={() => setShowForm(!showForm)}
        className="toggle-form-btn"
      >
        {showForm ? "Hide Form" : "Add Progress"}
      </button>

      {showForm && (
        <form onSubmit={handleSubmit} className="workout-form">
          <div>
            <label>Workout Description: <span style={{ color: '#666' }}>*</span></label>
            <input
              type="text"
              value={workoutDescription}
              onChange={(e) => setWorkoutDescription(e.target.value)}
              placeholder="Enter workout description"
              required
            />
          </div>
          <div>
            <label>Start Time: <span style={{ color: '#666' }}>*</span></label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>
          <div>
            <label>End Time: <span style={{ color: '#666' }}>*</span></label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>

          <div>
            <button
              type="button"
              onClick={calculateDuration}
              className="calculate-duration-btn"
            >
              Calculate Duration
            </button>
          </div>

          <div>
            <label>Duration:</label>
            <input
              type="text"
              value={duration}
              readOnly
              placeholder="HH:MM:SS"
            />
          </div>
          <div>
            <label>Date: <span style={{ color: '#666' }}>*</span></label>
            <input
              type="date"
              value={calendarDate}
              onChange={(e) => setCalendarDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? "Adding..." : "Add Progress"}
          </button>
        </form>
      )}

      {formattedData.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Day</th>
              <th>Workout Description</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Duration (mins)</th>
            </tr>
          </thead>
          <tbody>
            {formattedData.map((workout, index) => (
              <tr key={index}>
                <td>{workout.fullDate}</td>
                <td>{workout.day}</td>
                <td>{workout.workout_description}</td>
                <td>{workout.start_time}</td>
                <td>{workout.end_time}</td>
                <td>{workout.duration.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default WorkoutHistory;
